// ─── PhotoExport.js ───────────────────────────────────────────────────────────
// Downloads all project photos as a zip file with folder-per-area structure.
// Images renamed: AreaName_01.jpg, AreaName_02_COVER.jpg, AreaName_03_DEFECT.jpg
import JSZip from 'jszip';
import { showToast } from './Toast';

function sanitise(name) {
  return (name || 'Unnamed').replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_').slice(0, 40);
}

async function fetchAsBlob(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}`);
  return res.blob();
}

export async function exportPhotosAsZip(project, onProgress) {
  const areas = project.areas || [];
  const zip = new JSZip();
  const projectName = sanitise(project.projectName || project.clientName || project.jobNumber || 'Project');
  let totalPhotos = 0;
  let fetched = 0;

  // Count total
  areas.forEach(a => { totalPhotos += (a.photos || []).length; });
  if (totalPhotos === 0) {
    showToast('No photos to export', 'warning');
    return;
  }

  onProgress?.(`Downloading ${totalPhotos} photos...`);

  // Track folder names to avoid duplicates
  const folderCounts = {};

  for (const area of areas) {
    const photos = area.photos || [];
    if (!photos.length) continue;

    const typePrefix = area.type === 'internal' ? 'INT' : 'EXT';
    let baseName = `${typePrefix}_${sanitise(area.name)}`;

    // Deduplicate folder names
    if (folderCounts[baseName]) {
      folderCounts[baseName]++;
      baseName = `${baseName}_${folderCounts[baseName]}`;
    } else {
      folderCounts[baseName] = 1;
    }

    const folder = zip.folder(baseName);

    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      const num = String(i + 1).padStart(2, '0');
      let suffix = '';
      if (photo.isProjectHero) suffix = '_COVER';
      else if (photo.isDefect) suffix = '_DEFECT';

      const fileName = `${baseName}_${num}${suffix}.jpg`;

      try {
        const blob = await fetchAsBlob(photo.url);
        folder.file(fileName, blob);
        fetched++;
        onProgress?.(`Downloaded ${fetched} of ${totalPhotos}...`);
      } catch (err) {
        console.warn(`Failed to fetch photo: ${photo.url}`, err);
      }
    }
  }

  if (fetched === 0) {
    showToast('Could not download any photos', 'error');
    return;
  }

  onProgress?.('Creating zip file...');
  const blob = await zip.generateAsync({ type: 'blob' }, (meta) => {
    onProgress?.(`Compressing... ${Math.round(meta.percent)}%`);
  });

  // Download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${projectName}_Photos.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  onProgress?.(`✓ ${fetched} photos exported`);
}
